import {Link} from 'react-router-dom'
import {motion} from 'framer-motion'
import {twJoin} from 'tailwind-merge'
import {useRef} from 'react'

import {noop} from '@sdox/utils'
import {scrollToElementOnClick} from '@sdox/utils/dom'

import {PulseVariants} from '../../animations'
import {MediaPart} from './MediaPart'
import {BadgePart} from './BadgePart'
import {IndicatorPart} from './IndicatorPart'
import {Breakpoint, useBreakpoint} from '../../hooks'

import type {MediaPartProps} from './MediaPart'
import type {MouseEventHandler} from 'react'
import type {LinkProps} from 'react-router-dom'

const MotionLink = motion(Link)

export interface FolderItemProps extends MediaPartProps {
  handleAnimationComplete?: () => void
  heading?: string
  id: string
  isSelected?: boolean
  itemCount?: number
  label?: string
  shouldHighlight?: boolean
  to: LinkProps['to']
  linkProps?: Omit<typeof MotionLink, 'type'>
}

export const FolderItem = ({
  handleAnimationComplete = noop,
  heading = '',
  id,
  imageProps,
  isSelected = false,
  itemCount,
  label = '',
  shouldHighlight = false,
  to,
  type = 'media',
  linkProps,
}: FolderItemProps) => {
  const ref = useRef<HTMLAnchorElement | null>(null)
  const isDesktop = useBreakpoint(Breakpoint.MD_AND_UP)

  const onClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    if (!ref.current) return
    scrollToElementOnClick({element: ref.current})(event)
  }

  return (
    <MotionLink
      ref={ref}
      to={to}
      id={id}
      data-cy={'teaser'}
      aria-current={isSelected ? 'page' : false}
      variants={PulseVariants}
      initial={'invisible'}
      className={twJoin(
        'group relative rounded-radius-lg',
        'bg-interactive-surface-filled-tile-default',
        'scroll-m-[115px]',
      )}
      onAnimationComplete={handleAnimationComplete}
      animate={shouldHighlight ? 'pulse' : 'visible'}
      exit={'fade'}
      // we prevent the scroll reset on desktop because the transition to another document would look weird otherwise
      preventScrollReset={isDesktop}
      aria-labelledby={`folder-item-heading-${id}`}
      aria-describedby={`folder-item-label-${id}`}
      onClick={onClick}
      {...linkProps}
    >
      <IndicatorPart isSelected={isSelected} />
      <div
        className={twJoin(
          'overflow-hidden rounded-radius-lg',
          'group-hover:bg-scrim-primary/[0.04] group-hover:mix-blend-multiply',
          'group-active:bg-scrim-primary/[0.08] group-active:mix-blend-multiply',
        )}
      >
        <div className={'relative'}>
          {itemCount && <BadgePart itemCount={itemCount} />}
          <MediaPart
            type={type}
            imageProps={imageProps}
          />
        </div>
        <p className={'flex flex-col items-start p-spacing-16'}>
          <span
            id={`folder-item-heading-${id}`}
            className={'w-full truncate text-p1-emphasized md:text-p2-emphasized'}
          >
            {heading}
          </span>
          <span
            id={`folder-item-label-${id}`}
            className={'w-full truncate text-p1 md:text-p2'}
          >
            {label}
          </span>
        </p>
      </div>
    </MotionLink>
  )

}
